/**
 * The app's ONE `@repo/client/i18n` instance: the `LocaleStore` over
 * Capacitor Preferences, and the hooks built on it (`useLocale`,
 * `useErrorMessage`).
 *
 * ── WHY IT LIVES IN `features/auth` ─────────────────────────────────────────
 * Nothing else in the app had created it when T47 needed `useErrorMessage`
 * and the locale for `requestOtpInput`, and T47 may only write under
 * `features/auth`. The shell imports this feature eagerly (`SessionGate`), so
 * this module runs at boot, which is what `createI18n` needs: it overwrites
 * Paraglide's `getLocale` so every compiled message reads the stored field.
 *
 * `createI18n` must be called ONCE — a second call is a second store, and the
 * two disagree the moment either is written. Every other feature (T51's
 * language setting included) imports `appI18n` from here, or the lead moves
 * this file to `src/i18n.ts` and repoints the imports.
 */
import {
  createI18n,
  createKeyValueLocalePersistence,
  type KeyValueStorage,
} from "@repo/client/i18n";
import { preferences } from "../../native";

const preferencesStorage: KeyValueStorage = {
  getItem: (key) => preferences.get(key),
  removeItem: (key) => preferences.remove(key),
  setItem: (key, value) => preferences.set(key, value),
};

function preferredLocales(): readonly string[] {
  if (typeof navigator === "undefined") {
    return [];
  }
  return navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];
}

export const appI18n = createI18n({
  onPersistError: (error) => {
    console.warn("[i18n] the language setting was not saved", error);
  },
  persistence: createKeyValueLocalePersistence(preferencesStorage),
  preferredLocales,
});
